(() => {
    if (customElements.get('main-product-v2')) return;

    class MainProductV2 extends HTMLElement {
      connectedCallback() {
        this.toggleButtons = Array.from(this.querySelectorAll('[data-main-product-v2-toggle]'));
        this.thumbnailButtons = Array.from(this.querySelectorAll('[data-main-product-v2-thumbnail]'));
        this.gallery = this.querySelector('[data-main-product-v2-gallery]');
        this.thumbnailRail = this.querySelector('[data-main-product-v2-thumbnail-rail]');
        this.thumbnailViewport = this.querySelector('[data-main-product-v2-thumbnail-viewport]');
        this.thumbnailTrack = this.querySelector('[data-main-product-v2-thumbnail-track]');
        this.previousThumbnailButton = this.querySelector('[data-main-product-v2-thumbnail-previous]');
        this.nextThumbnailButton = this.querySelector('[data-main-product-v2-thumbnail-next]');
        this.nutritionModalTriggers = Array.from(this.querySelectorAll('[data-main-product-v2-nutrition-trigger]'));
        this.nutritionModals = Array.from(this.querySelectorAll('[data-main-product-v2-nutrition-modal]'));
        this.nutritionModalCloseButtons = Array.from(this.querySelectorAll('[data-main-product-v2-nutrition-close]'));
        this.activeNutritionModal = null;
        this.activeNutritionTrigger = null;
        this.previousNutritionDocumentOverflow = '';
        this.videoTestimonialTriggers = Array.from(this.querySelectorAll('[data-main-product-v2-video-trigger]'));
        this.videoTestimonialModals = Array.from(this.querySelectorAll('[data-main-product-v2-video-modal]'));
        this.videoTestimonialCloseButtons = Array.from(this.querySelectorAll('[data-main-product-v2-video-close]'));
        this.videoTestimonialSoundButtons = Array.from(this.querySelectorAll('[data-main-product-v2-video-sound]'));
        this.activeVideoTestimonialModal = null;
        this.activeVideoTestimonialTrigger = null;
        this.mediaStage = this.querySelector('[data-main-product-v2-media-stage]');
        this.mobileSupportDestination = this.querySelector('[data-main-product-v2-mobile-support-destination]');
        this.mobileSupportBlocks = Array.from(this.querySelectorAll('[data-main-product-v2-mobile-support]'));
        this.mobileSupportOrigins = this.mobileSupportBlocks.map((block) => {
          const marker = document.createComment('main-product-v2-support-origin');
          block.parentNode.insertBefore(marker, block);
          return { block, marker };
        });
        this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
        this.stackedLayoutMediaQuery = window.matchMedia('(max-width: 989px)');
        this.reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.currentThumbnailIndex = Math.max(
          0,
          this.thumbnailButtons.findIndex((button) => button.getAttribute('aria-current') === 'true')
        );
        this.thumbnailHasOverflow = false;
        this.mediaPointerStart = null;

        this.toggleButtons.forEach((button) => {
          button.addEventListener('click', this.handleToggle);
        });

        this.thumbnailButtons.forEach((button) => {
          button.addEventListener('click', this.handleThumbnail);
        });

        this.previousThumbnailButton?.addEventListener('click', this.handlePreviousThumbnail);
        this.nextThumbnailButton?.addEventListener('click', this.handleNextThumbnail);
        this.mediaStage?.addEventListener('pointerdown', this.handleMediaPointerDown);
        this.mediaStage?.addEventListener('pointerup', this.handleMediaPointerUp);
        this.mediaStage?.addEventListener('pointercancel', this.handleMediaPointerCancel);
        this.nutritionModalTriggers.forEach((trigger) => trigger.addEventListener('click', this.handleNutritionModalTrigger));
        this.nutritionModals.forEach((modal) => modal.addEventListener('click', this.handleNutritionModalOverlayClick));
        this.nutritionModalCloseButtons.forEach((button) => button.addEventListener('click', this.handleNutritionModalClose));
        document.addEventListener('keydown', this.handleNutritionModalKeydown);
        this.videoTestimonialTriggers.forEach((trigger) => trigger.addEventListener('click', this.handleVideoTestimonialTrigger));
        this.videoTestimonialModals.forEach((modal) => modal.addEventListener('click', this.handleVideoTestimonialOverlayClick));
        this.videoTestimonialCloseButtons.forEach((button) => button.addEventListener('click', this.handleVideoTestimonialClose));
        this.videoTestimonialSoundButtons.forEach((button) => button.addEventListener('click', this.handleVideoTestimonialSound));
        document.addEventListener('keydown', this.handleVideoTestimonialKeydown);
        this.desktopMediaQuery.addEventListener('change', this.handleGalleryResize);
        this.stackedLayoutMediaQuery.addEventListener('change', this.handleSupportLayout);
        window.addEventListener('resize', this.handleStudiesResize);

        this.querySelectorAll('.main_product_v2__media-image').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.handleGalleryResize, { once: true });
        });

        this.studyCertificationImages = Array.from(this.querySelectorAll('.main_product_v2__certification-image'));
        this.studyCertificationImages.forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.handleStudiesResize);
        });

        if (this.mediaStage && 'ResizeObserver' in window) {
          this.galleryResizeObserver = new ResizeObserver(this.handleGalleryResize);
          this.galleryResizeObserver.observe(this.mediaStage);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }

        this.updateSupportLayout();
        requestAnimationFrame(() => this.refreshThumbnailLayout(false));
      }

      disconnectedCallback() {
        this.toggleButtons?.forEach((button) => {
          button.removeEventListener('click', this.handleToggle);
        });

        this.thumbnailButtons?.forEach((button) => {
          button.removeEventListener('click', this.handleThumbnail);
        });

        this.previousThumbnailButton?.removeEventListener('click', this.handlePreviousThumbnail);
        this.nextThumbnailButton?.removeEventListener('click', this.handleNextThumbnail);
        this.mediaStage?.removeEventListener('pointerdown', this.handleMediaPointerDown);
        this.mediaStage?.removeEventListener('pointerup', this.handleMediaPointerUp);
        this.mediaStage?.removeEventListener('pointercancel', this.handleMediaPointerCancel);
        this.nutritionModalTriggers?.forEach((trigger) => trigger.removeEventListener('click', this.handleNutritionModalTrigger));
        this.nutritionModals?.forEach((modal) => modal.removeEventListener('click', this.handleNutritionModalOverlayClick));
        this.nutritionModalCloseButtons?.forEach((button) => button.removeEventListener('click', this.handleNutritionModalClose));
        document.removeEventListener('keydown', this.handleNutritionModalKeydown);
        this.closeNutritionModal(this.activeNutritionModal, false);
        this.videoTestimonialTriggers?.forEach((trigger) => trigger.removeEventListener('click', this.handleVideoTestimonialTrigger));
        this.videoTestimonialModals?.forEach((modal) => modal.removeEventListener('click', this.handleVideoTestimonialOverlayClick));
        this.videoTestimonialCloseButtons?.forEach((button) => button.removeEventListener('click', this.handleVideoTestimonialClose));
        this.videoTestimonialSoundButtons?.forEach((button) => button.removeEventListener('click', this.handleVideoTestimonialSound));
        document.removeEventListener('keydown', this.handleVideoTestimonialKeydown);
        this.closeVideoTestimonialModal(this.activeVideoTestimonialModal, false);
        this.desktopMediaQuery?.removeEventListener('change', this.handleGalleryResize);
        this.stackedLayoutMediaQuery?.removeEventListener('change', this.handleSupportLayout);
        window.removeEventListener('resize', this.handleStudiesResize);
        this.studyCertificationImages?.forEach((image) => {
          image.removeEventListener('load', this.handleStudiesResize);
        });
        this.galleryResizeObserver?.disconnect();
        if (this.studiesResizeFrame) cancelAnimationFrame(this.studiesResizeFrame);
        this.variantUnsubscriber?.();
      }

      handleThumbnail = (event) => {
        const button = event.currentTarget;
        this.activateMedia(button.dataset.mainProductV2Thumbnail, {
          alignThumbnail: true,
          smooth: true,
        });
      };

      handlePreviousThumbnail = () => {
        this.selectThumbnailByIndex(this.currentThumbnailIndex - 1);
      };

      handleNextThumbnail = () => {
        this.selectThumbnailByIndex(this.currentThumbnailIndex + 1);
      };

      handleMediaPointerDown = (event) => {
        if (
          this.desktopMediaQuery.matches ||
          !event.isPrimary ||
          (event.pointerType === 'mouse' && event.button !== 0) ||
          event.target.closest('video, iframe, model-viewer, button, a')
        ) {
          return;
        }

        this.mediaPointerStart = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
        this.mediaStage?.setPointerCapture?.(event.pointerId);
      };

      handleMediaPointerUp = (event) => {
        const start = this.mediaPointerStart;
        this.mediaPointerStart = null;
        if (!start || start.pointerId !== event.pointerId) return;

        this.mediaStage?.releasePointerCapture?.(event.pointerId);
        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;
        const swipeThreshold = 42;

        if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;

        this.selectThumbnailByIndex(this.currentThumbnailIndex + (deltaX < 0 ? 1 : -1));
      };

      handleMediaPointerCancel = (event) => {
        if (this.mediaPointerStart?.pointerId === event.pointerId) {
          this.mediaPointerStart = null;
        }
      };

      handleNutritionModalTrigger = (event) => {
        const trigger = event.currentTarget;
        const modalId = trigger.getAttribute('aria-controls');
        const modal = modalId ? this.querySelector(`#${CSS.escape(modalId)}`) : null;
        if (modal) this.openNutritionModal(modal, trigger);
      };

      handleNutritionModalOverlayClick = (event) => {
        if (event.target === event.currentTarget) this.closeNutritionModal(event.currentTarget);
      };

      handleNutritionModalClose = (event) => {
        this.closeNutritionModal(event.currentTarget.closest('[data-main-product-v2-nutrition-modal]'));
      };

      handleNutritionModalKeydown = (event) => {
        if (event.key === 'Escape' && this.activeNutritionModal) {
          event.preventDefault();
          this.closeNutritionModal(this.activeNutritionModal);
        }
      };

      openNutritionModal(modal, trigger) {
        if (this.activeNutritionModal && this.activeNutritionModal !== modal) {
          this.closeNutritionModal(this.activeNutritionModal, false);
        }

        if (!modal.__mainProductV2NutritionOrigin) {
          modal.__mainProductV2NutritionOrigin = { parent: modal.parentNode, nextSibling: modal.nextSibling };
        }
        if (modal.parentNode !== document.body) document.body.append(modal);
        this.activeNutritionModal = modal;
        this.activeNutritionTrigger = trigger;
        this.previousNutritionDocumentOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => modal.querySelector('[data-main-product-v2-nutrition-close]')?.focus());
      }

      closeNutritionModal(modal, returnFocus = true) {
        if (!modal) return;
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        const origin = modal.__mainProductV2NutritionOrigin;
        if (origin?.parent) origin.parent.insertBefore(modal, origin.nextSibling);
        const trigger = this.activeNutritionTrigger;
        document.documentElement.style.overflow = this.previousNutritionDocumentOverflow;
        this.activeNutritionModal = null;
        this.activeNutritionTrigger = null;
        this.previousNutritionDocumentOverflow = '';
        if (returnFocus) trigger?.focus();
      }

      handleVideoTestimonialTrigger = (event) => {
        const trigger = event.currentTarget;
        const modalId = trigger.getAttribute('aria-controls');
        const modal = modalId ? this.querySelector(`#${CSS.escape(modalId)}`) : null;
        if (modal && trigger.dataset.mainProductV2VideoSrc) this.openVideoTestimonialModal(modal, trigger);
      };

      handleVideoTestimonialOverlayClick = (event) => {
        if (event.target === event.currentTarget) this.closeVideoTestimonialModal(event.currentTarget);
      };

      handleVideoTestimonialClose = (event) => {
        this.closeVideoTestimonialModal(event.currentTarget.closest('[data-main-product-v2-video-modal]'));
      };

      handleVideoTestimonialSound = (event) => {
        const modal = event.currentTarget.closest('[data-main-product-v2-video-modal]');
        const video = modal?.querySelector('[data-main-product-v2-modal-video]');
        if (!video) return;
        video.muted = !video.muted;
        this.updateVideoTestimonialSoundState(modal);
      };

      handleVideoTestimonialKeydown = (event) => {
        if (event.key === 'Escape' && this.activeVideoTestimonialModal) {
          event.preventDefault();
          this.closeVideoTestimonialModal(this.activeVideoTestimonialModal);
        }
      };

      openVideoTestimonialModal(modal, trigger) {
        if (this.activeVideoTestimonialModal && this.activeVideoTestimonialModal !== modal) {
          this.closeVideoTestimonialModal(this.activeVideoTestimonialModal, false);
        }

        const video = modal.querySelector('[data-main-product-v2-modal-video]');
        if (!video) return;

        this.activeVideoTestimonialModal = modal;
        this.activeVideoTestimonialTrigger = trigger;
        video.pause();
        video.removeAttribute('src');
        video.src = trigger.dataset.mainProductV2VideoSrc;
        video.muted = true;
        video.defaultMuted = true;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        this.updateVideoTestimonialSoundState(modal);

        const playVideo = () => video.play().catch(() => {});
        video.addEventListener('canplay', playVideo, { once: true });
        video.load();
        playVideo();
        requestAnimationFrame(() => modal.querySelector('[data-main-product-v2-video-close]')?.focus());
      }

      closeVideoTestimonialModal(modal, returnFocus = true) {
        if (!modal) return;
        const video = modal.querySelector('[data-main-product-v2-modal-video]');
        video?.pause();
        if (video) {
          video.removeAttribute('src');
          video.load();
        }
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        const trigger = this.activeVideoTestimonialTrigger;
        this.activeVideoTestimonialModal = null;
        this.activeVideoTestimonialTrigger = null;
        if (returnFocus) trigger?.focus();
      }

      updateVideoTestimonialSoundState(modal) {
        const video = modal?.querySelector('[data-main-product-v2-modal-video]');
        const button = modal?.querySelector('[data-main-product-v2-video-sound]');
        if (!video || !button) return;
        const isUnmuted = !video.muted;
        button.setAttribute('aria-pressed', String(isUnmuted));
        button.setAttribute('aria-label', isUnmuted ? 'Mute video' : 'Unmute video');
      }

      handleGalleryResize = () => {
        requestAnimationFrame(() => this.refreshThumbnailLayout(true));
      };

      handleSupportLayout = () => {
        this.updateSupportLayout();
      };

      handleStudiesResize = () => {
        if (this.studiesResizeFrame) cancelAnimationFrame(this.studiesResizeFrame);
        this.studiesResizeFrame = requestAnimationFrame(() => {
          this.querySelectorAll('.main_product_v2__studies--expanded').forEach((studies) => {
            const panel = studies.querySelector('.main_product_v2__expand-panel');
            if (panel) this.layoutStudyCertifications(panel);
          });
        });
      };

      handleVariantChange = ({ data }) => {
        if (data.sectionId !== this.dataset.sectionId) return;

        if (data.variant?.featured_media?.id) {
          this.activateMedia(data.variant.featured_media.id, {
            alignThumbnail: true,
            smooth: true,
          });
        }

        const sourceContent = data.html?.querySelector('.main_product_v2__one-time-content');
        const currentContent = this.querySelector('.main_product_v2__one-time-content');
        if (sourceContent && currentContent) {
          currentContent.innerHTML = sourceContent.innerHTML;
          currentContent.className = sourceContent.className;
        }

        const sourceSoldOut = data.html?.querySelector('.main_product_v2__one-time-sold-out');
        const currentSoldOut = this.querySelector('.main_product_v2__one-time-sold-out');
        if (sourceSoldOut && currentSoldOut) {
          currentSoldOut.innerHTML = sourceSoldOut.innerHTML;
          currentSoldOut.className = sourceSoldOut.className;
        }

        const sourceButton = data.html?.querySelector('.main_product_v2__one-time-button');
        const currentButton = this.querySelector('.main_product_v2__one-time-button');
        if (sourceButton && currentButton) {
          currentButton.setAttribute('aria-label', sourceButton.getAttribute('aria-label') || '');
        }
      };

      handleToggle = async (event) => {
        const button = event.currentTarget;
        const panelId = button.getAttribute('aria-controls');
        const panel = this.querySelector(`#${CSS.escape(panelId)}`);
        if (!panel) return;

        const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
        const accordion = button.closest('.main_product_v2__accordion');

        if (accordion) {
          if (shouldOpen) {
            const openAccordionButtons = Array.from(
              this.querySelectorAll(
                '.main_product_v2__accordion-button[aria-expanded="true"]'
              )
            ).filter((openButton) => openButton !== button);

            await Promise.all(
              openAccordionButtons.map((openButton) => this.closeAccordion(openButton))
            );
            this.openAccordion(button, panel);
          } else {
            await this.closeAccordion(button);
          }
          return;
        }

        const studies = button.classList.contains('main_product_v2__expand-button')
          ? button.closest('.main_product_v2__studies')
          : null;
        button.setAttribute('aria-expanded', String(shouldOpen));
        panel.setAttribute('aria-hidden', String(!shouldOpen));
        panel.inert = !shouldOpen;

        if (shouldOpen) {
          studies?.classList.remove('main_product_v2__studies--closing');
          studies?.classList.add('main_product_v2__studies--expanded');
          panel.classList.remove('main_product_v2__panel--closing');
          panel.classList.add('main_product_v2__panel--open');
          if (studies) this.layoutStudyCertifications(panel);
          panel.style.height = `${panel.scrollHeight}px`;
          const finishOpen = (transitionEvent) => {
            if (transitionEvent.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', finishOpen);
            if (button.getAttribute('aria-expanded') === 'true') {
              panel.style.height = 'auto';
              if (studies) this.layoutStudyCertifications(panel);
            }
          };
          panel.addEventListener('transitionend', finishOpen);
        } else {
          panel.style.height = `${panel.scrollHeight}px`;
          studies?.classList.add('main_product_v2__studies--closing');
          requestAnimationFrame(() => {
            if (studies) {
              panel.classList.add('main_product_v2__panel--closing');
            } else {
              panel.classList.remove('main_product_v2__panel--open');
            }
            panel.style.height = '0px';
          });

          if (studies) {
            let collapseComplete = false;
            const finishCollapse = () => {
              if (collapseComplete) return;
              collapseComplete = true;
              if (button.getAttribute('aria-expanded') === 'true') {
                studies.classList.remove('main_product_v2__studies--closing');
                panel.classList.remove('main_product_v2__panel--closing');
                panel.removeEventListener('transitionend', handleCollapseTransition);
                return;
              }
              studies.classList.remove(
                'main_product_v2__studies--expanded',
                'main_product_v2__studies--closing'
              );
              panel.classList.remove('main_product_v2__panel--open', 'main_product_v2__panel--closing');
              panel.removeEventListener('transitionend', handleCollapseTransition);
            };
            const handleCollapseTransition = (transitionEvent) => {
              if (transitionEvent.propertyName === 'height') finishCollapse();
            };

            panel.addEventListener('transitionend', handleCollapseTransition);
            window.setTimeout(finishCollapse, this.reducedMotionMediaQuery.matches ? 0 : 450);
          }
        }
      };

      openAccordion(button, panel) {
        button.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        panel.inert = false;
        panel.classList.remove('main_product_v2__panel--closing');
        panel.classList.add('main_product_v2__panel--open');
        panel.style.height = `${panel.scrollHeight}px`;

        const finishOpen = (transitionEvent) => {
          if (transitionEvent && transitionEvent.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', finishOpen);
          if (button.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
        };

        panel.addEventListener('transitionend', finishOpen);
        if (this.reducedMotionMediaQuery.matches) finishOpen();
      }

      closeAccordion(button) {
        const panelId = button.getAttribute('aria-controls');
        const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
        if (!panel || button.getAttribute('aria-expanded') !== 'true') return Promise.resolve();

        button.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
        panel.style.height = `${panel.scrollHeight}px`;

        return new Promise((resolve) => {
          let finished = false;
          const finishClose = () => {
            if (finished) return;
            finished = true;
            panel.classList.remove(
              'main_product_v2__panel--open',
              'main_product_v2__panel--closing'
            );
            panel.style.height = '0px';
            panel.removeEventListener('transitionend', handleTransitionEnd);
            resolve();
          };
          const handleTransitionEnd = (transitionEvent) => {
            if (transitionEvent.propertyName === 'height') finishClose();
          };

          panel.addEventListener('transitionend', handleTransitionEnd);
          requestAnimationFrame(() => {
            panel.classList.add('main_product_v2__panel--closing');
            panel.classList.remove('main_product_v2__panel--open');
            panel.style.height = '0px';
            if (this.reducedMotionMediaQuery.matches) finishClose();
          });
          window.setTimeout(finishClose, this.reducedMotionMediaQuery.matches ? 0 : 350);
        });
      }

      layoutStudyCertifications(panel) {
        const certifications = panel.querySelector('.main_product_v2__certifications--expanded');
        const exclusion = panel.querySelector('.main_product_v2__certification-exclusion');
        const content = panel.querySelector('.main_product_v2__expand-content');
        if (!certifications || !exclusion || !content) return;

        exclusion.style.setProperty('--main-product-v2-expanded-content-height', '0px');

        const certificationHeight = Math.max(
          0,
          ...Array.from(certifications.children).map((certification) => certification.offsetHeight)
        );
        const certificationItems = Array.from(certifications.children);
        const certificationGap = parseFloat(getComputedStyle(certifications).columnGap) || 0;
        const certificationWidth =
          certificationItems.reduce((total, certification) => total + certification.offsetWidth, 0) +
          Math.max(0, certificationItems.length - 1) * certificationGap;
        exclusion.style.setProperty(
          '--main-product-v2-certification-exclusion-height',
          `${certificationHeight}px`
        );
        exclusion.style.setProperty(
          '--main-product-v2-certification-exclusion-width',
          `${certificationWidth}px`
        );

        let contentHeight = Math.max(content.scrollHeight, certificationHeight);
        for (let pass = 0; pass < 4; pass += 1) {
          exclusion.style.setProperty('--main-product-v2-expanded-content-height', `${contentHeight}px`);
          const nextHeight = Math.max(content.scrollHeight, certificationHeight);
          if (Math.abs(nextHeight - contentHeight) < 1) break;
          contentHeight = nextHeight;
        }

        exclusion.style.setProperty('--main-product-v2-expanded-content-height', `${contentHeight}px`);
        if (panel.style.height && panel.style.height !== 'auto') {
          panel.style.height = `${panel.scrollHeight}px`;
        }
      }

      updateSupportLayout() {
        if (!this.mobileSupportDestination || !this.mobileSupportOrigins.length) return;

        if (this.stackedLayoutMediaQuery.matches) {
          this.mobileSupportOrigins.forEach(({ block }) => {
            this.mobileSupportDestination.append(block);
          });
          return;
        }

        this.mobileSupportOrigins.forEach(({ block, marker }) => {
          marker.parentNode?.insertBefore(block, marker.nextSibling);
        });
      }

      selectThumbnailByIndex(index) {
        if (!this.thumbnailButtons.length) return;

        const boundedIndex = Math.min(Math.max(index, 0), this.thumbnailButtons.length - 1);
        const button = this.thumbnailButtons[boundedIndex];

        this.activateMedia(button.dataset.mainProductV2Thumbnail, {
          alignThumbnail: true,
          smooth: true,
        });
      }

      activateMedia(mediaId, { alignThumbnail = true, smooth = true, animate = true } = {}) {
        const mediaPanels = Array.from(this.querySelectorAll('[data-main-product-v2-media]'));
        const activePanel = mediaPanels.find((panel) => panel.dataset.mainProductV2Media === String(mediaId));
        if (!activePanel) return;

        const nextThumbnailIndex = this.thumbnailButtons.findIndex(
          (button) => button.dataset.mainProductV2Thumbnail === String(mediaId)
        );
        const direction =
          nextThumbnailIndex > this.currentThumbnailIndex
            ? 'next'
            : nextThumbnailIndex < this.currentThumbnailIndex
              ? 'previous'
              : null;

        mediaPanels.forEach((panel) => {
          const isActive = panel === activePanel;
          panel.hidden = !isActive;
          panel.classList.remove(
            'main_product_v2__media-panel--slide-next',
            'main_product_v2__media-panel--slide-previous'
          );

          if (!isActive) {
            panel.querySelectorAll('video').forEach((video) => video.pause());
          }
        });

        if (animate && direction && !this.reducedMotionMediaQuery.matches) {
          const animationClass = `main_product_v2__media-panel--slide-${direction}`;
          activePanel.classList.add(animationClass);
          activePanel.addEventListener(
            'animationend',
            () => activePanel.classList.remove(animationClass),
            { once: true }
          );
        }

        this.thumbnailButtons?.forEach((button, index) => {
          const isActive = button.dataset.mainProductV2Thumbnail === String(mediaId);
          button.setAttribute('aria-current', String(isActive));
          if (isActive) this.currentThumbnailIndex = index;
        });

        this.updateThumbnailNavigation();

        if (alignThumbnail) {
          requestAnimationFrame(() => {
            this.refreshThumbnailLayout(smooth);
          });
        }
      }

      refreshThumbnailLayout(smooth = false) {
        if (!this.gallery || !this.thumbnailButtons.length || !this.thumbnailViewport || !this.thumbnailTrack) return;

        if (!this.desktopMediaQuery.matches) {
          this.thumbnailHasOverflow = this.thumbnailTrack.scrollWidth > this.thumbnailViewport.clientWidth + 1;
          this.thumbnailTrack.style.removeProperty('--main-product-v2-thumbnail-trailing-space');
          this.thumbnailTrack.style.removeProperty('--main-product-v2-thumbnail-offset');
          this.scrollMobileThumbnailIntoView(this.currentThumbnailIndex, smooth);
          this.updateThumbnailNavigation();
          return;
        }

        if (!this.mediaStage || !this.thumbnailRail) return;

        this.thumbnailTrack.style.setProperty('--main-product-v2-thumbnail-trailing-space', '0px');
        this.thumbnailHasOverflow = this.thumbnailTrack.scrollHeight > this.thumbnailViewport.clientHeight + 1;

        if (this.thumbnailHasOverflow) {
          this.positionDesktopThumbnail(this.currentThumbnailIndex, smooth);
        } else {
          this.thumbnailTrack.style.setProperty('--main-product-v2-thumbnail-offset', '0px');
        }
        this.updateThumbnailNavigation();
      }

      positionDesktopThumbnail(index, smooth = true) {
        const button = this.thumbnailButtons[index];
        if (!button || !this.thumbnailTrack || !this.thumbnailViewport) return;

        if (!smooth) {
          this.thumbnailTrack.style.transition = 'none';
        }

        const maximumOffset = Math.max(0, this.thumbnailTrack.scrollHeight - this.thumbnailViewport.clientHeight);
        const centeredOffset = button.offsetTop - Math.max(0, (this.thumbnailViewport.clientHeight - button.offsetHeight) / 2);
        const offset = Math.min(Math.max(centeredOffset, 0), maximumOffset);
        this.thumbnailTrack.style.setProperty('--main-product-v2-thumbnail-offset', `${-offset}px`);

        if (!smooth) {
          this.thumbnailTrack.getBoundingClientRect();
          requestAnimationFrame(() => {
            this.thumbnailTrack.style.removeProperty('transition');
          });
        }
      }

      scrollMobileThumbnailIntoView(index, smooth = true) {
        const button = this.thumbnailButtons[index];
        if (!button || !this.thumbnailViewport) return;

        const maximumScroll = Math.max(0, this.thumbnailViewport.scrollWidth - this.thumbnailViewport.clientWidth);
        const centeredScroll = button.offsetLeft - Math.max(0, (this.thumbnailViewport.clientWidth - button.offsetWidth) / 2);
        const scrollLeft = Math.min(Math.max(centeredScroll, 0), maximumScroll);

        this.thumbnailViewport.scrollTo({
          left: scrollLeft,
          behavior: smooth && !this.reducedMotionMediaQuery.matches ? 'smooth' : 'auto',
        });
      }

      updateThumbnailNavigation() {
        const lastIndex = this.thumbnailButtons.length - 1;

        if (this.previousThumbnailButton) {
          this.previousThumbnailButton.disabled = this.currentThumbnailIndex <= 0;
        }

        if (this.nextThumbnailButton) {
          this.nextThumbnailButton.disabled = this.currentThumbnailIndex >= lastIndex;
        }
      }
    }

    customElements.define('main-product-v2', MainProductV2);
  })();
